/**
 * Analytics Module
 * Handles Google Analytics 4 tracking and user interaction analytics
 */

const GA_MEASUREMENT_ID = document.querySelector('meta[name="ga-measurement-id"]')?.content || '';

/**
 * Initialize analytics tracking
 */
export function initializeAnalytics() {
    configureGoogleAnalytics();
    trackUserInteractions();
}

/**
 * Configure Google Analytics 4
 */
function configureGoogleAnalytics() {
    if (typeof gtag !== 'undefined') {
        gtag('config', GA_MEASUREMENT_ID, {
            page_title: 'Burger Prime',
            page_location: window.location.href
        });
    }
}

/**
 * Track user interactions for analytics
 */
function trackUserInteractions() {
    trackButtonClicks();
    trackFormSubmissions();
}

/**
 * Track button clicks
 */
function trackButtonClicks() {
    document.addEventListener('click', handleButtonClick);
}

/**
 * Handle button click events for analytics
 * @param {MouseEvent} e - Click event
 */
function handleButtonClick(e) {
    const button = e.target.closest('button, .btn');
    if (button) {
        trackEvent('click', {
            event_category: 'engagement',
            event_label: button.textContent.trim() || button.className
        });
    }
}

/**
 * Track form submissions
 */
function trackFormSubmissions() {
    document.addEventListener('submit', handleFormSubmit);
}

/**
 * Handle form submission events for analytics
 * @param {Event} e - Submit event
 */
function handleFormSubmit(e) {
    trackEvent('form_submit', {
        event_category: 'engagement',
        event_label: e.target.id || 'unknown_form'
    });
}

/**
 * Track custom event to Google Analytics
 * @param {string} eventName - Event name
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
    if (typeof gtag !== 'undefined') {
        gtag('event', eventName, params);
    }
}

/**
 * Track page view
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export function trackPageView(pagePath, pageTitle) {
    if (typeof gtag !== 'undefined') {
        gtag('config', GA_MEASUREMENT_ID, {
            page_path: pagePath,
            page_title: pageTitle
        });
    }
}

/**
 * Track custom conversion event
 * @param {string} conversionId - Conversion ID
 * @param {Object} params - Conversion parameters
 */
export function trackConversion(conversionId, params = {}) {
    if (typeof gtag !== 'undefined') {
        gtag('event', 'conversion', {
            send_to: conversionId,
            ...params
        });
    }
}

/**
 * Track e-commerce events
 * @param {string} action - E-commerce action (view_item, add_to_cart, purchase, etc.)
 * @param {Object} items - Item data
 */
export function trackEcommerce(action, items) {
    if (typeof gtag !== 'undefined') {
        gtag('event', action, {
            items: items
        });
    }
}

/**
 * Set user properties for analytics
 * @param {Object} properties - User properties
 */
export function setUserProperties(properties) {
    if (typeof gtag !== 'undefined') {
        gtag('set', 'user_properties', properties);
    }
}

/**
 * Set user ID for cross-device tracking
 * @param {string} userId - User ID
 */
export function setUserId(userId) {
    if (typeof gtag !== 'undefined') {
        gtag('config', GA_MEASUREMENT_ID, {
            user_id: userId
        });
    }
}

/**
 * Cleanup analytics event listeners
 */
export function cleanupAnalytics() {
    document.removeEventListener('click', handleButtonClick);
    document.removeEventListener('submit', handleFormSubmit);
}
